import { createContext, useContext } from 'react';

/**
 * Two languages, and the direction that comes with each (founder, 2026-09-13: full English support,
 * with the system smart about right-to-left and left-to-right).
 *
 * Arabic is the product and English is the interface language people who do not read Arabic get;
 * `CLAUDE.md` says so and nothing here changes it. What changes is that English is now complete
 * rather than a fallback nobody finished, and that direction follows the language everywhere instead
 * of being written into the markup once.
 *
 * **The catalogue is distributed, not central.** Each component owns its own words through
 * `messages({ ar, en })`, which is typed so that a key added to one language and forgotten in the
 * other does not compile. A single file of four hundred strings would be read by nobody and would
 * put every wording change three files away from the markup it belongs to.
 */
export const LANGUAGES = Object.freeze(['ar', 'en'] as const);
export type Language = (typeof LANGUAGES)[number];

/** What the person chose. `auto` means "follow this browser", which is the default. */
export type LanguageChoice = Language | 'auto';

export function isLanguage(value: unknown): value is Language {
  return value === 'ar' || value === 'en';
}

export function isLanguageChoice(value: unknown): value is LanguageChoice {
  return value === 'auto' || isLanguage(value);
}

/**
 * The language a browser is asking for. Arabic wins whenever the person lists it at all, however far
 * down: someone whose phone is set to English but who also reads Arabic is here for the Arabic
 * product. Everyone else gets English, which is the honest reading of a browser that never mentions
 * Arabic — falling back to Arabic there would hand a page nobody can read to the person least able
 * to find the language setting.
 */
export function languageFor(choice: LanguageChoice, preferred: readonly string[] = []): Language {
  if (isLanguage(choice)) return choice;
  // Arabic unless the person says otherwise (founder, 2026-09-20: "arabic default"). It used to
  // fall back to English for a browser that never mentions Arabic, on the reasoning that handing an
  // unreadable page to the person least able to find the setting is worse than guessing wrong. The
  // product is Arabic-first and its own readers do not all advertise it, and the setting is no
  // longer buried: the introduction now opens with the choice, in both languages, before anything
  // else is read. `preferred` stays in the signature because the choice is still the person's.
  void preferred;
  return 'ar';
}

/** What the browser asks for, guarded: `navigator.languages` is absent in some environments. */
export function browserLanguages(): readonly string[] {
  try {
    const navigatorLanguages: unknown = globalThis.navigator.languages;
    if (Array.isArray(navigatorLanguages)) {
      const tags = navigatorLanguages.filter((tag): tag is string => typeof tag === 'string');
      if (tags.length > 0) return tags;
    }
    const single = globalThis.navigator.language;
    return typeof single === 'string' && single.length > 0 ? [single] : [];
  } catch {
    return [];
  }
}

export function directionOf(language: Language): 'rtl' | 'ltr' {
  return language === 'ar' ? 'rtl' : 'ltr';
}

/**
 * A component's own words in both languages. The parameter type makes the two sides the same shape,
 * so a string added to one and forgotten in the other is a compile error rather than a blank in the
 * interface of whichever language nobody on the team reads.
 */
export function messages<M extends object>(map: {
  readonly ar: M;
  readonly en: M;
}): {
  readonly ar: M;
  readonly en: M;
} {
  return map;
}

/**
 * The language in force, for components that are too deep to be handed it. Arabic is the default so
 * that a component rendered outside a provider — a test, a story — reads as the product does.
 */
export const LanguageContext = createContext<Language>('ar');

export function useLanguage(): Language {
  return useContext(LanguageContext);
}

/** The component's own words, in the language in force. */
export function useMessages<M extends object>(catalogue: { readonly ar: M; readonly en: M }): M {
  return catalogue[useLanguage()];
}
